import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export const CHART_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#06B6D4", "#EC4899", "#6366F1"];

const kfmt = (v) => {
  const n = Number(v);
  if (Math.abs(n) >= 1e7) return (n / 1e7).toFixed(1) + "Cr";
  if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(1) + "L";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(0) + "k";
  return String(n);
};

const axis = { fontSize: 11, stroke: "hsl(var(--muted-foreground))" };
const grid = { stroke: "hsl(var(--border))", strokeDasharray: "3 3" };
const tip = {
  contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12, color: "hsl(var(--foreground))" },
  labelStyle: { color: "hsl(var(--muted-foreground))" },
};

export function ChartCard({ title, subtitle, children, className = "", action }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-head text-sm font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function AreaTrend({ data, x, y, color = CHART_COLORS[0], height = 240, yLabel }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ left: 4, right: 8, top: 4 }}>
        <defs>
          <linearGradient id={`g-${y}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.35} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid {...grid} vertical={false} />
        <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis tick={axis} tickLine={false} axisLine={false} width={52} tickFormatter={kfmt} />
        <Tooltip {...tip} />
        <Area type="monotone" dataKey={y} stroke={color} strokeWidth={2} fill={`url(#g-${y})`} name={yLabel || y} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function Bars({ data, x, y, color = CHART_COLORS[1], height = 240, horizontal = false }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={horizontal ? "vertical" : "horizontal"} margin={{ left: horizontal ? 8 : 4, right: 8 }}>
        <CartesianGrid {...grid} vertical={false} />
        {horizontal ? <>
          <XAxis type="number" tick={axis} tickLine={false} axisLine={false} tickFormatter={kfmt} />
          <YAxis type="category" dataKey={x} tick={axis} tickLine={false} axisLine={false} width={96} />
        </> : <>
          <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} minTickGap={16} />
          <YAxis tick={axis} tickLine={false} axisLine={false} width={52} tickFormatter={kfmt} />
        </>}
        <Tooltip {...tip} cursor={{ fill: "hsl(var(--muted)/0.4)" }} />
        <Bar dataKey={y} fill={color} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function MultiLine({ data, x, lines, height = 260, dualAxis = false }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ left: 4, right: dualAxis ? 4 : 8, top: 4 }}>
        <CartesianGrid {...grid} vertical={false} />
        <XAxis dataKey={x} tick={axis} tickLine={false} axisLine={false} minTickGap={24} />
        <YAxis yAxisId="left" tick={axis} tickLine={false} axisLine={false} width={52} tickFormatter={kfmt} />
        {dualAxis && <YAxis yAxisId="right" orientation="right" tick={axis} tickLine={false} axisLine={false} width={52} tickFormatter={kfmt} />}
        <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map((l, i) => <Line key={l.key} yAxisId={dualAxis && i > 0 ? "right" : "left"} type="monotone" dataKey={l.key} name={l.name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} isAnimationActive={false} />)}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, dataKey = "value", nameKey = "name", height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey={dataKey} nameKey={nameKey} innerRadius={55} outerRadius={90} paddingAngle={3} isAnimationActive={false}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
